<template>
  <div v-if="selected">
    <div class="header">
      <div class="title">
        <h2>Plan Summary</h2>
        <p v-if="finalized" style="font-size: 18px">Your Plan Summary is a record of your plan performance and payments. Download a copy for your records.</p>
        <p v-else style="font-size: 18px">Your Plan Summary keeps you informed about your potential award. You will receive email reminders when new forecasts or payments are calculated.</p>
      </div>

      <div
        v-if="summary_message"
        class="message"
      >
        {{ summary_message }}
      </div>
    </div>

    <!-- toolbar -->
    <PlanToolbar
      class="toolbar"
      :pdf="selected.pdf_link"
      :statement="getStatementRoute()"
      :spd="spd_link"
    />

    <!-- DPSP election -->
    <div v-if="is_dpsp" class="dpsp-election">
      <div class="dpsp-election-description">{{ dpsp_description }}</div>
      <div v-if="dpsp_edit_text !== ''" class="dpsp-election-edit-block">
        <standard-button @click="editDpsp">{{ dpsp_edit_text }}</standard-button>
        <va-modal
          v-if="show_dpsp_edit"
          v-model="show_dpsp_edit"
          :title="dpsp_edit_text"
          close-button
          ok-text="Save"
          @ok="saveDpsp"
        >
          <div class="dpsp-edit-error" v-if="dpsp_edit_error != ''">{{ dpsp_edit_error }}</div>
          <div class="dpsp-edit-line">
            I elect to defer
            <input class="dpsp-percent-input" v-model="dpsp_edit_percent">
            % of my {{ dpsp_year }} bonus.
          </div>
          <div class="dpsp-edit-line">
            The deferral will be paid out starting
            <select class="dpsp-event-select" v-model="dpsp_edit_event">
              <option value="0">Select ...</option>
              <option value="4">in the year {{ dpsp_year + 4 }}</option>
              <option value="5">in the year {{ dpsp_year + 5 }}</option>
              <option value="6">in the year {{ dpsp_year + 6 }}</option>
              <option value="7">in the year {{ dpsp_year + 7 }}</option>
              <option value="8">in the year {{ dpsp_year + 8 }}</option>
              <option value="9">in the year {{ dpsp_year + 9 }}</option>
              <option value="10">in the year {{ dpsp_year + 10 }}</option>
              <option value="11">in the year {{ dpsp_year + 11 }}</option>
              <option value="12">in the year {{ dpsp_year + 12 }}</option>
              <option value="13">in the year {{ dpsp_year + 13 }}</option>
              <option value="14">in the year {{ dpsp_year + 14 }}</option>
              <option value="T">on separation from service</option>
            </select>
            .
          </div>
          <div class="dpsp-edit-line">
            Payment will be
            <select class="dpsp-frequency-select" v-model="dpsp_edit_frequency">
              <option value="0">Select ...</option>
              <option value="1">as a lump sum</option>
              <option value="2">in 2 annual installments</option>
              <option value="3">in 3 annual installments</option>
              <option value="4">in 4 annual installments</option>
              <option value="5">in 5 annual installments</option>
              <option value="6">in 6 annual installments</option>
              <option value="7">in 7 annual installments</option>
              <option value="8">in 8 annual installments</option>
              <option value="9">in 9 annual installments</option>
              <option value="10">in 10 annual installments</option>
            </select>
            .
          </div>
        </va-modal>
      </div>
      <div v-if="dpsp_past.length > 0" class="dpsp-election-past-block">
        <h3>Past Elections</h3>
        <table class="dpsp-election-past-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Pay Type</th>
              <th>Election Date</th>
              <th>Percent</th>
              <th>Payment Start</th>
              <th>Payment Method</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, index) in dpsp_past" :key="index">
              <td>{{ p.year }}</td>
              <td class="dpsp-election-past-td-center">{{ p.pay_type }}</td>
              <td class="dpsp-election-past-td-center">{{ p.election_date }}</td>
              <td class="dpsp-election-past-td-center">{{ p.percent }}</td>
              <td class="dpsp-election-past-td-center">{{ p.payment_start }}</td>
              <td class="dpsp-election-past-td-center">{{ p.payment_method }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- overview -->
    <PlanCard class="overview-card">
      <div class="overview">
        <!-- Current/Forecast Header -->
        <div class="summary-header">
          <h2 class="sidebar-header">
            Current Value
          </h2>
          <h2 class="overview-header">
            Forecasted Value
          </h2>
        </div>
        <!-- Sidebar -->
        <div class="sidebar">
          <div class="grant-groups-container">
            <div class="grant-groups-header">
              <div v-if="selected.valuation_factor_choice != 'pct-of-company'">
                <h3>
                  <QuestionBadge :id="'pendo-overview-phantom-share-price'">
                    Phantom&nbsp;Share<br>Redemption&nbsp;Price
                  </QuestionBadge>
                </h3>
                <p class="text-highlight text-center font-bold text-md">
                  {{ formatCurrency(selected.current_phantom_share_price, 2) }}
                </p>
              </div>
              <div v-if="selected.valuation_factor_choice == 'pct-of-company'">
                <h3>
                  <QuestionBadge :id="'pendo-overview-percent-of-company-shares'">
                    Total&nbsp;Percent<br>Of&nbsp;Company
                  </QuestionBadge>
                </h3>
                <p class="text-highlight text-center font-bold text-md">
                  {{ selected.certificates.reduce((cuml, value) => cuml + selected.projections[value.group_index]['target'].annual_grants[value.grant_year], 0).toFixed(selected.fractional_part) }}%
                </p>
              </div>
              <h3 class="current-vested-unvested">
                <QuestionBadge :id="'pendo-overview-current-vested-unvested'">
                  Vested&nbsp;Value&nbsp;/<br>Unvested&nbsp;Value
                </QuestionBadge>
              </h3>
            </div>
            <div class="grant-groups-footer">
              Click on any link below to view your award.
            </div>
            <div class="grant-groups-body">
              <template
                v-for="(certificate, index) in selected.certificates"
                :key="`${index}`"
              >
                <div>
                  <a
                    v-if="certificate.use_certificate"
                    class="grant-group-name"
                    :href="getCertificateRoute(certificate)"
                    target="_blank"
                  >{{ certificate.name + (selected.valuation_factor_choice == 'pct-of-company' ? `&nbsp;-&nbsp;${planValueForecast.annual_grants[certificate.grant_year].toFixed(selected.fractional_part)}%` : '') }}</a>
                  <span v-else>{{ certificate.name }}</span>
                  <p class="grant-group-values">
                    {{ formatCurrency(certificate.vested) }}<br>{{ formatCurrency(certificate.unvested) }}
                  </p>
                </div>
              </template>
            </div>
          </div>

          <div class="totals">
            <h3>Vested</h3>
            <p>{{ formatCurrency(selected.certificates.reduce((cuml, cert) => { cuml += cert.vested; return cuml; }, 0)) }}</p>

            <h3>Unvested</h3>
            <p>{{ formatCurrency(selected.certificates.reduce((cuml, cert) => { cuml += cert.unvested; return cuml; }, 0)) }}</p>

            <h3>Paid&nbsp;to&nbsp;date</h3>
            <p>{{ formatCurrency(selected.cumulative_redemptions) }}</p>

            <h3>Total&nbsp;Plan&nbsp;Value</h3>
            <p>{{ formatCurrency(selected.cumulative_redemptions + selected.certificates.reduce((cuml, cert) => { cuml += cert.vested + cert.unvested; return cuml; }, 0)) }}</p>
          </div>
          <div
            v-if="selected.certificates.some((certificate) => certificate.use_certificate)"
            class="grant-groups-footer-mobile"
          >
            Select any award below to view the certificate.
          </div>
          <div class="grant-groups-body-mobile">
            <select
              v-if="selected.certificates.some((certificate) => certificate.use_certificate)"
              v-model="selected_certificate"
            >
              <option :value="null" />
              <template
                v-for="(certificate, index) in selected.certificates" 
                :key="index"
              >
                <option
                  v-if="certificate.use_certificate"
                  :value="certificate"
                >
                  {{ certificate.name }}
                </option>
              </template>
            </select>
          </div>
        </div>
        <!-- Graph Header -->
        <h2 class="overview-header-mobile">
          Forecasted Value
        </h2>
        <div
          class="header"
          :class="(selected.grants_type?.cicOnly || selected.grants_type?.sfsOnly) ? 'grid-cols-2' : 'grid-cols-3'"
        >
          <div>
            <h3>
              <QuestionBadge :id="'pendo-overview-next-payment-date'">
                Next<br>Maturity
              </QuestionBadge>
            </h3>
            <p class="text-highlight text-center font-bold text-md">
              <template v-if="selected.grants_type?.cicOnly">
                Change In Control
              </template>
              <template v-else-if="selected.grants_type?.sfsOnly">
                Separation From Service
              </template>
              <template v-else>
                {{ formatDate(selected.next_payment_date) }}
              </template>
            </p>
          </div>
          <div v-if="!(selected.grants_type?.cicOnly || selected.grants_type?.sfsOnly)">
            <h3>
              <QuestionBadge :id="'pendo-overview-forecasted-payment'">
                Forecasted<br>Payment
              </QuestionBadge>
            </h3>
            <p class="text-highlight text-center font-bold text-md">
              {{ formatCurrency(selected.forecasted_payment) }}
            </p>
          </div>
          <div>
            <h3>
              <QuestionBadge :id="'pendo-overview-total-plan-value'">
                Plan&nbsp;Value<br>(EOY)
              </QuestionBadge>
            </h3>
            <p>{{ formatCurrency(selected.total_plan_value) }}</p>
          </div>
        </div>
        <!-- Graph -->
        <div class="graph-content">
          <template v-if="userDataLoaded">
            <LongTermLineChart
              :only-target="clientIsDiy"
              :history="selected.graph_data"
            />
          </template>
        </div>
      </div>
    </PlanCard>

    <!-- shares -->
    <div class="shares">
      <h2>TOTAL PLAN VALUE FORECAST</h2>
      <p>Total Plan Value includes Vested Plan Value, Unvested Value, and Cumulative Redemptions paid to date. The forecast displays potential non-guaranteed awards in future years. Actual awards will be determined annually by the company.</p>
      <p
        v-if="!clientIsDiy"
        style="margin-top:0.5rem;"
      >
        Use the drop-down menus to select the performance forecast and award type (if applicable) you would like to view.
      </p>

      <PlanCard class="shares-card">
        <!-- header -->
        <div class="shares-header">
          <template v-if="!clientIsDiy">
            <div>
              <h3>Award<br>Type</h3>
              <select
                v-model="selected_award_type"
                style="text-transform:capitalize;"
              >
                <option
                  v-for="group_index in computedAwardGroups"
                  :key="group_index"
                  :value="group_index"
                  style="text-transform:capitalize;"
                >
                  {{ selected.projections[group_index][selected_tier].grant_group_name }}
                </option>
              </select>
            </div>
            <div>
              <h3>Performance<br>Forecast</h3>
              <select
                v-model="selected_tier"
                style="text-transform:capitalize;"
              >
                <option
                  v-if="showBase"
                  value="base"
                  style="text-transform:capitalize;"
                >
                  {{ selected.projections[selected_award_type].base.tier_label }}
                </option>
                <option
                  v-if="showTarget"
                  value="target"
                  style="text-transform:capitalize;"
                >
                  {{ selected.projections[selected_award_type].target.tier_label }}
                </option>
                <option
                  v-if="showSuperior"
                  value="superior"
                  style="text-transform:capitalize;"
                >
                  {{ selected.projections[selected_award_type].superior.tier_label }}
                </option>
              </select>
            </div>
          </template>
        </div>

        <!-- body -->
        <div class="body">
          <table id="projections-table">
            <thead>
              <tr>
                <th>
                  <template v-if="planValueForecast.completed">
                    <input
                      v-model="show_completed"
                      type="checkbox"
                    >&nbsp;
                    Show Completed Years
                  </template>
                </th>
                <template v-if="show_completed">
                  <th
                    v-for="year in planValueForecast.completed.years"
                    :key="year"
                  >
                    <span
                      class="prev-year"
                      :class="{disabled:selected_year == planValueForecast.completed.years[0]}"
                      @click="selectPrevYear"
                    >&lt;</span>
                    {{ year }}
                    <span
                      class="next-year"
                      :class="{disabled:selected_year == lastProjectionYear}"
                      @click="selectNextYear"
                    >&gt;</span>
                  </th>
                </template>
                <th
                  v-for="year in Object.keys(planValueForecast.total_plan_value)"
                  :key="year"
                >
                  <span
                    class="prev-year"
                    :class="{disabled:(show_completed ? selected_year == planValueForecast.completed.years[0] : selected_year == firstProjectionYear)}"
                    @click="selectPrevYear"
                  >&lt;</span>
                  {{ year }}
                  <span
                    class="next-year"
                    :class="{disabled:selected_year == lastProjectionYear}"
                    @click="selectNextYear"
                  >&gt;</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="planValueForecast.formula_value">
                <td>
                  <QuestionBadge :id="'pendo-forecast-formula-value'">
                    Formula Value
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.formula_value)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.formula_value)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
              <tr v-if="planValueForecast.total_phantom_shares && selected.valuation_factor_choice != 'pct-of-company'">
                <td>
                  <QuestionBadge :id="'pendo-forecast-phantom-shares'">
                    Total Shares
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.total_phantom_shares)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatNumber(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.total_phantom_shares)"
                  :key="`${key}-${value}`"
                >
                  {{ formatNumber(value) }}
                </td>
              </tr>
              <tr v-if="planValueForecast.redemption_price && selected.valuation_factor_choice != 'pct-of-company'">
                <td>
                  <QuestionBadge :id="'pendo-forecast-redemption-price'">
                    Redemption Price (EOY)
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.redemption_price)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value, 2) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.redemption_price)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value, 2) }}
                </td>
              </tr>
              <tr v-if="planValueForecast.issue_price && selected.separate_redemption_share_price && selected.valuation_factor_choice != 'pct-of-company'">
                <td>
                  <QuestionBadge :id="'pendo-forecast-issue-price'">
                    Issue Price (EOY)
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.issue_price)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value, 2) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.issue_price)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value, 2) }}
                </td>
              </tr>
              <tr :class="selected.valuation_factor_choice != 'pct-of-company' ? 'border-t' : ''">
                <td>
                  <QuestionBadge :id="'pendo-forecast-annual-awards'">
                    {{ selected.valuation_factor_choice != 'pct-of-company' ? 'Annual Awards' : 'Percentage Awarded (BOY)' }}
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.annual_grants)"
                    :key="`${key}-${value}`"
                  >
                    {{ selected.valuation_factor_choice != 'pct-of-company' ? formatNumber(value) : (value.toFixed(selected.fractional_part) + '%') }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.annual_grants)"
                  :key="`${key}-${value}`"
                >
                  {{ selected.valuation_factor_choice != 'pct-of-company' ? formatNumber(value) : (value.toFixed(selected.fractional_part) + '%') }}
                </td>
              </tr>
              <tr v-if="selected.valuation_factor_choice == 'pct-of-company'">
                <td>
                  <QuestionBadge :id="'pendo-forecast-total-percentage-awarded'">
                    Total Percentage Awarded (BOY)
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.annual_grants)"
                    :key="`${key}-${value}`"
                  >
                    {{ getCumulativePercentage(key).toFixed(selected.fractional_part) }}%
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.annual_grants)"
                  :key="`${key}-${value}`"
                >
                  {{ getCumulativePercentage(key).toFixed(selected.fractional_part) }}%
                </td>
              </tr>
              <tr>
                <td>
                  <QuestionBadge :id="'pendo-forecast-projected-redemptions'">
                    Redemptions
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.annual_distributions)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.annual_distributions)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
              <tr>
                <td>
                  <QuestionBadge :id="'pendo-forecast-cumulative-redemptions'">
                    Cumulative Redemptions
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.cumulative_distributions)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.cumulative_distributions)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
              <tr>
                <td>
                  <QuestionBadge :id="'pendo-forecast-vested-eoy'">
                    Vested Value (EOY)
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.vested_value)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.vested_value)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
              <tr>
                <td>
                  <QuestionBadge :id="'pendo-forecast-unvested-eoy'">
                    Unvested Value (EOY)
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.unvested_value)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.unvested_value)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <QuestionBadge :id="'pendo-forecast-total-plan-value'">
                    Total Plan Value
                  </QuestionBadge>
                </td>
                <template v-if="show_completed">
                  <td
                    v-for="[key, value] in Object.entries(planValueForecast.completed.total_plan_value)"
                    :key="`${key}-${value}`"
                  >
                    {{ formatCurrency(value) }}
                  </td>
                </template>
                <td
                  v-for="[key, value] in Object.entries(planValueForecast.total_plan_value)"
                  :key="`${key}-${value}`"
                >
                  {{ formatCurrency(value) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- footer -->
        <div class="footer">
          <p style="font-size: 14px;">The values, figures, and projections in this plan summary are based on hypothetical assumptions about future company performance and are provided solely for illustrative purposes. They are intended to demonstrate how the plan may operate under certain scenarios and should not be interpreted as guaranteed or expected outcomes. Illustrative plan values reflect assumed financial performance, including potential growth rates, award schedules, and market conditions that may or may not occur. These assumptions are speculative and subject to uncertainty, risks, and other variables beyond the company&CloseCurlyQuote;s control. This Plan Summary should not be interpreted as a guarantee of future compensation.</p>
          <p style="font-size: 14px;">Participation in this Phantom Stock Plan does not entitle participants to any future awards or specific payment amounts. Payment values, if any, will be determined according to the terms of the plan at the time of a qualifying vesting or payment event and may differ materially &mdash; including being substantially lower or resulting in no payout &mdash; than the figures shown. Participants are encouraged to consult their own financial, tax, or legal advisors regarding their specific circumstances.</p>
          <p style="font-size: 14px;">In the event of any inconsistency between this summary and the official plan documents, the formal plan documents will govern. The company reserves the right to modify, suspend, or terminate the plan in accordance with those governing documents.</p>
        </div>
      </PlanCard>
    </div>
  </div>
</template>

<script>
import Moment from 'moment';
import StandardButton from "@/components/elements/StandardButton";
import PlanToolbar from "@/components/plans/global/PlanToolbar.vue";
import PlanCard from "@/components/plans/global/PlanCard.vue";
import LongTermLineChart from "@/components/plans/long-term/charts/LongTermLineChart.vue";
import UtilsMixin from "@/mixins/UtilsMixin";
import QuestionBadge from "@/components/pendo/QuestionBadge.vue";
import ColorsMixin from "@/mixins/ColorsMixin";
import VLcurrency from "@/composables/VLcurrency";
import VLdate from "@/composables/VLdate";
import VLnumber from "@/composables/VLnumber";

export default {
  name: "LongTermPlanView",

  components: {
    StandardButton,
    QuestionBadge,
    LongTermLineChart,
    PlanCard,
    PlanToolbar
  },

  mixins: [ UtilsMixin, ColorsMixin ],

  props: {
    plan: {
      type: Object,
      required: true,
    },
    finalized: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selected: null,
      selected_year: null,
      selected_certificate: null,
      selected_award_type: null,
      selected_tier: 'target',
      show_completed: false,
      today_string: null,
      dpsp_year: null,
      dpsp_present: false,
      dpsp_percent: null,
      dpsp_distribution_election_date: null,
      dpsp_distribution_start_date: null,
      dpsp_event: null,
      dpsp_frequency: null,
      dpsp_number_of_payments: null,
      dpsp_description: '',
      dpsp_edit_text: '',
      is_dpsp: false,
      show_dpsp_edit: false,
      dpsp_edit_error: '',
      dpsp_edit_percent: '',
      dpsp_edit_event: '0',
      dpsp_edit_frequency: '0',
    }
  },

  computed: {
    userDataLoaded() {
      return this.$dataDirectory?.userDataLoaded
    },
    userData() {
      return this.$dataDirectory?.userData
    },
    computedAwardGroups() {
      // If there are only two groups return the first one, no need for combined
      // return an empty array if projections don't exist
      if(this.selected?.projections) {
        let groups = Object.keys(this.selected.projections)

        if(groups.length === 2) {
          return [groups[0]]
        }
        else {
          return groups
        }
      }
      else {
        return [];
      }
    },
    planValueForecast() {
      return this.selected.projections[this.selected_award_type][this.selected_tier]
    },
    summary_message() {
      return this.plan.current?.summary_message;
    },
    spd_link() {
      return this.selected?.spd_link;
    },
    showBase() {
      return (this.selected?.option_parameters?.show_base ?? false) && (this.selected?.projections[this.selected_award_type]?.base)
    },
    showTarget() {
      return (this.selected?.option_parameters?.show_target ?? false) && (this.selected?.projections[this.selected_award_type]?.target)
    },
    showSuperior() {
      return (this.selected?.option_parameters?.show_superior ?? false) && (this.selected?.projections[this.selected_award_type]?.superior)
    },
    dpsp_past() {
      let past = []
      const rawPast = this.plan?.dpspData.past
      if (rawPast) {
        rawPast.forEach((p) => {
          past.push({
            year: p.year,
            pay_type: p.pay_type_name,
            election_date: this.formatDate(p.distribution_election_date),
            percent: p.percent,
            payment_start: p.event == 'Termination Event' ? 'SFS' : p.distribution_start_date.slice(0, 4),
            payment_method: p.frequency == 'Annual' ? (p.number_of_payments + ' installments') : 'lump sum',
          })
        })
      }
      return past;
    },
    firstProjectionYear() {
      return this.plan?.current.summary_data.projections[this.selected_award_type][this.selected_tier].years[0];
    },
    lastProjectionYear() {
      let projectionYears = this.plan?.current.summary_data.projections[this.selected_award_type][this.selected_tier].years;
      return projectionYears[projectionYears.length - 1];
    },
  },

  watch: {
    selected_year: {
      handler() {
        this.formatTable();
      },
    },
    selected_award_type: {
      handler(newVal) {
        this.selected_year = this.plan?.current.summary_data.projections[newVal][this.selected_tier].years[0];
      },
    },
    selected_tier: {
      handler(newVal) {
        this.selected_year = this.plan?.current.summary_data.projections[this.selected_award_type][newVal].years[0];
      },
    },
    selected_certificate: {
      handler(newVal) {
        if (newVal) window.open(this.getCertificateRoute(newVal), '_blank')
      }
    },
    show_completed: {
      handler(newVal) {
        if (!newVal && this.selected_year < this.firstProjectionYear) this.selected_year = this.firstProjectionYear;
      }
    }
  },

  beforeCreate() {},

  mounted() {
    this.selected = this.plan?.current.summary_data

    this.selected_award_type = this.plan?.current.summary_data.grant_group_count === 1 ? 0 : this.plan?.current.summary_data.grant_group_count

    this.today_string = Moment().format('YYYY-MM-DD')
    if (this.plan?.dpspData) {
      this.dpsp_year = this.plan?.dpspData.year
      if (this.dpsp_year) {
        this.importDpspData(this.plan?.dpspData)
      }
      this.is_dpsp = true
    } else {
      this.is_dpsp = false
    }
    this.show_dpsp_edit = false
  },

  methods: {
    formatCurrency(value, decimals = 0, currency = 'USD') {
      return VLcurrency.formatCurrency(value, decimals, currency);
    },
    formatDate(d, format = 'YYYY-MM-DD', preset = 'DEFAULT') {
      return VLdate.formatDate(d, format, preset);
    },
    formatNumber(value, decimals = 0) {
      return VLnumber.formatNumber(value, decimals);
    },
    getCertificateRoute(certificate) {
      return this.$apiManager.route(`grant-certificate/${certificate.granted_shares_id}`);
    },
    getStatementRoute() {
      let plan_id = this.plan?.current.summary_data.plan_id;
      let route = this.$apiManager.route(`plan-statement/${plan_id}`);

      return route;
    },
    /**
     * Calculates the cumulative percentage of annual grants up to and including the specified year.
     * 
     * @param {string} year - The target year to calculate cumulative percentage up to
     * @returns {number} The sum of all annual grants from the first recorded year through the specified year
     */
    getCumulativePercentage(year) {
        // Get all years for which we have annual grant data
        const years = Object.keys(this.planValueForecast.annual_grants);
        
        // Find the position of the target year in our array of years
        const index = years.indexOf(year);
        
        // Take all years from the beginning up to and including the target year,
        // then sum up the annual grant values for those years
        return years.slice(0, index + 1).reduce((sum, y) => 
            sum + this.planValueForecast.annual_grants[y], 0);
    },
    importDpspData(dpspData) {
      if (!dpspData.present) {
        this.dpsp_present = false
        this.dpsp_description = 'You have not yet made an election to defer a portion of your ' + this.dpsp_year + ' bonus.'
        this.dpsp_edit_text = 'Make Election'
      } else {
        this.dpsp_present = true
        this.dpsp_percent = dpspData.percent
        this.dpsp_distribution_election_date = dpspData.distribution_election_date
        this.dpsp_distribution_start_date = dpspData.distribution_start_date
        this.dpsp_event = dpspData.event
        this.dpsp_frequency = dpspData.frequency
        this.dpsp_number_of_payments = dpspData.number_of_payments
        const event_pretty = this.dpsp_event === 'Termination Event' ? 'on separation from service' : ('in the year ' + this.dpsp_distribution_start_date.slice(0, 4))
        const how_paid = this.dpsp_frequency === 'Annual' ? ('starting ' + event_pretty + ' in ' + this.dpsp_number_of_payments + ' annual installments') : (event_pretty + ' as a lump sum')
        this.dpsp_description = 'On ' +
          VLdate.formatDate(this.dpsp_distribution_election_date) +
          ', you elected to defer ' +
          this.dpsp_percent +
          '% of your ' +
          this.dpsp_year +
          ' bonus, to be paid out ' +
          how_paid + '.'
        this.dpsp_edit_text = 'Edit Election'
      }
    },
    editDpsp() {
      this.dpsp_edit_error = ''
      if (this.dpsp_present) {
        this.dpsp_edit_percent = this.dpsp_percent.toString()
        if (this.dpsp_event === 'Termination Event') {
          this.dpsp_edit_event = 'T'
        } else {
          this.dpsp_edit_event = (parseInt(this.dpsp_distribution_start_date.slice(0, 4)) - this.dpsp_year).toString()
        }
        if (this.dpsp_frequency === 'Annual') {
          this.dpsp_edit_frequency = this.dpsp_number_of_payments.toString()
        } else {
          this.dpsp_edit_frequency = '1'
        }
      } else {
        this.dpsp_edit_percent = ''
        this.dpsp_edit_event = '0'
        this.dpsp_edit_frequency = '0'
      }
      this.show_dpsp_edit = true
    },
    saveDpsp() {
      let post_data = {}
      let dpsp_data = {}
      this.dpsp_edit_error = ''
      if (this.dpsp_edit_percent === '') {
        this.dpsp_edit_error = 'Please enter the % of bonus.'
      } else if (/^\d+$/.test(this.dpsp_edit_percent)) {
        post_data.percent = parseInt(this.dpsp_edit_percent)
        if (post_data.percent < 0 || post_data.percent > 100) {
          this.dpsp_edit_error = 'The % of bonus must be between 0 and 100.'
        } else if (post_data.percent === 0) {
          dpsp_data.present = false
        } else {
          dpsp_data.present = true
          dpsp_data.percent = post_data.percent
        }
      } else {
        this.dpsp_edit_error = 'The % of bonus must be a whole number.'
      }
      if (this.dpsp_edit_error === '' && dpsp_data.present) {
        if (this.dpsp_edit_event === '0') {
          this.dpsp_edit_error = 'Please select when the deferral will be paid out.'
        } else {
          if (this.dpsp_edit_event === 'T') {
            post_data.event = 'Termination Event'
            post_data.distribution_start_date = null
          } else {
            post_data.event = 'In Service Distribution'
            post_data.distribution_start_date = (this.dpsp_year + parseInt(this.dpsp_edit_event)).toString() + '-01-01'
          }
          dpsp_data.event = post_data.event
          dpsp_data.distribution_start_date = post_data.distribution_start_date
          if (this.dpsp_edit_frequency === '0') {
            this.dpsp_edit_error = 'Please select how the deferral will be paid out.'
          } else {
            if (this.dpsp_edit_frequency === '1') {
              post_data.frequency = 'Lump Sum'
              post_data.number_of_payments = 1
            } else {
              post_data.frequency = 'Annual'
              post_data.number_of_payments = parseInt(this.dpsp_edit_frequency)
            }
            dpsp_data.frequency = post_data.frequency
            dpsp_data.number_of_payments = post_data.number_of_payments
          }
        }
      }
      if (this.dpsp_edit_error === '') {
        dpsp_data.distribution_election_date = this.today_string
        this.$apiManager.post('dpsp-election/' + this.plan?.current.summary_data.plan_id, post_data).then(
          (pack) => this.dpspUpdated(pack, dpsp_data),
          () => this.dpspFailure(),
	)
      } else {
        this.show_dpsp_edit = true
      }
    },
    dpspUpdated(pack, dpsp_data) {
      if (pack.data && pack.data.success) {
        this.importDpspData(dpsp_data)
      } else {
        this.dpsp_edit_error = 'There was a problem updating your DPSP election data.'
        this.show_dpsp_edit = true
      }
    },
    dpspFailure() {
      this.dpsp_edit_error = 'There was a problem connecting to the server.'
      this.show_dpsp_edit = true
    },
    formatTable() {
      const table = document.getElementById("projections-table");
      if (!table) {
        setTimeout(() => this.formatTable(), 1000);
        return;
      }
      this.$nextTick(() => {
        const yearIndex = 1 + this.selected_year - (this.show_completed ? this.planValueForecast.completed.years[0] : this.firstProjectionYear);
        table.childNodes.forEach((section) => {
          section.childNodes.forEach((row) => {
            let elKey = 0; // keeps track of elements, ignores text nodes
            row.childNodes.forEach((cell, key) => {
              if (key !== 0 && elKey !== yearIndex) {
                cell.classList?.remove('active');
              } else if (cell.nodeType != 3 && cell.nodeType != 8) { // ignore text nodes
                cell.classList?.add('active');
              }
              if (cell.nodeType != 3 && cell.nodeType != 8) { // ignore text nodes
                elKey++;
              }
            });
          });
        });
      })
    },
    selectPrevYear() {
      if (this.show_completed ? this.selected_year != this.planValueForecast.completed.years[0] : this.selected_year != this.firstProjectionYear) {
        this.selected_year--;
      }
    },
    selectNextYear() {
      if (this.selected_year != this.lastProjectionYear) {
        this.selected_year++;
      }
    },
  },
}
</script>

<style scoped lang="scss">
@use "../../scss/_variables.scss";

.dpsp-election {
  border: 1px solid var(--charcoal-tint-20);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}

.dpsp-election-edit-block {
  padding: 10px;
}

.dpsp-edit-error {
  color: red;
  padding: 5px;
}

.dpsp-edit-line {
  padding: 5px;
}

.dpsp-percent-input {
  display: inline-block;
  border: 1px solid blue;
  border-radius: 8px;
  width: 50px;
  padding: 5px;
}

.dpsp-event-select {
  display: inline-block;
  border: 1px solid blue;
  border-radius: 8px;
  width: 218px;
  padding: 5px;
}

.dpsp-frequency-select {
  display: inline-block;
  border: 1px solid blue;
  border-radius: 8px;
  width: 205px;
  padding: 5px;
}

.dpsp-election-past-block {
  margin-top: 20px;
}

.dpsp-election-past-table tbody,
.dpsp-election-past-table th,
.dpsp-election-past-table td {
  border: none;
}

.dpsp-election-past-td-center {
  text-align: center;
}

.header {
  display: flex;
  justify-content: space-between;

  .title {

    h1 {
      font-size: variables.$text-xl; // ~ 32px
      line-height: 1.0625;
      font-weight: 700;
      margin-bottom: 0;
    }
    h2 {
      text-transform: capitalize;
      margin-bottom: 0;
    }
  }

  .message {
    min-width: 400px;
    width: 50%;

    border: 1px solid var(--charcoal-tint-20);
    border-radius: 8px;
    padding: 0.5rem 1rem;
  }
}

h3 {
  font-size: variables.$text-base;
}

.text-highlight {
  color: var(--highlight-color);
}

.font-bold {
  font-weight: bold;
}

.text-md {
  font-size: variables.$text-md;
}

.text-center {
  text-align: center;
}

.overview-card {
  margin-bottom: 4rem;
}

.overview {
  @media screen and (min-width: 1300px) {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  width: 100%;

  .header {
    grid-area: 2 / 3 / 3 / 8;

    display: grid;

    grid-gap: 1rem;

    padding: 1.5rem;

    border-bottom: 1px solid variables.$charcoal-tint-20;

    &>div {
      display: flex;
      flex-direction: column;

      justify-content: center;
      align-items: center;

      &:last-child {
        justify-content: flex-end;
      }
    }

    h3 {
      text-align: center;
      font-weight: bold;
      font-size: variables.$text-base;

      margin-bottom: 0.25rem;
    }
    p {
      color: var(--highlight-color);
      font-weight: bold;
      font-size: variables.$text-md;
    }
  }

  .grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  .grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }

  .graph-content {
    grid-area: 3 / 3 / 4 / 8;

    height: 560px;
    width: 100%;

    padding: 1.5rem;
  }

  .summary-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-area: 1 / 1 / 2 / 8;
    background-color: var(--brand-color);

    .sidebar-header {
      grid-area: 1 / 1 / 2 / 3;      
    }

    .overview-header {
      grid-area: 1 / 3 / 2 / 8;      
    }

    h2 {
      color: var(--foreground-color);
      text-align: center;
      margin-top: 0.5vw;
    }
  }

  .sidebar {
    grid-area: 2 / 1 / 4 / 3;

    padding: 1.5rem;

    border-right: 1px solid variables.$charcoal-tint-20;

    display: flex;
    flex-direction: column;

    .grant-groups-container {
      display: flex;
      flex-direction: column;
      flex-grow: 1;

      padding-bottom: 0.75rem;
      margin-bottom: 0.75rem;

      border-bottom: 1px solid variables.$charcoal-tint-20;

      .grant-groups-header {
        display: flex;
        justify-content: space-between;

        padding-bottom: 1.5rem;
        margin-bottom: 0.75rem;

        border-bottom: 1px solid variables.$charcoal-tint-20;


        &>div {
          display: flex;
          flex-direction: column;

          justify-content: center;
          align-items: center;

          &:last-child {
            justify-content: flex-end;
          }
        }

        h3 {
          font-size: variables.$text-base;

          margin-bottom: 0.25rem;
          text-align: center;
          font-weight: bold;
        }
      }

      .grant-groups-body {
        display: flex;
        flex-direction: column;

        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;

        max-height: 38vh;

        div {
          display: grid;
          grid-template-columns: 3fr 2fr;
          grid-column-gap: 1rem;

          margin-bottom: 0.5rem;

          height: 100%;
          width: 100%;

          .grant-group-name {
            font-weight: bold;
            font-size: variables.$text-base;
          }

          .grant-group-values {
            font-size: variables.$text-base;
            text-align: right;
            padding-right: 1em;
          }
        }
      }

      .grant-groups-footer {
        text-align: center;
        margin-bottom: 12px;
        text-wrap-style: balance;
      }
    }

    .totals {
      display: grid;
      grid-template-columns: 1fr 2fr;

      grid-column-gap: 1.5rem;
      grid-row-gap: 0.25rem;

      h3 {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        margin: 0;

        font-weight: bold;
        font-size: variables.$text-base;
      }

      p {
        color: var(--highlight-color);
        font-weight: bold;
        font-size: variables.$text-md;

        text-align: right;
        padding-right: 1rem;
      }
    }

    .grant-certificates {
      display: grid;
      grid-template-rows: 15% 70% 15%;

      grid-row-gap: 0.4rem;

      height: 100%;

      h3 {
        font-weight: bold;
        font-size: variables.$text-base;

        margin-bottom: 0;
      }

      .certificate-links {
        display: flex;
        flex-direction: column;

        font-size: variables.$text-md;

        a {
          font-size: variables.$text-base;
          margin-bottom: 0.6rem;
        }
      }

      .pagination {
        display: flex;
        justify-content: flex-end;
        font-size: variables.$text-sm;

        button {
          border: none;
          border-radius: 5px;
          background: none;
          padding: 0 0.5rem;
          cursor: pointer;

          &:hover {
            background-color: variables.$charcoal-tint-20;
          }
        }

        div {
          margin: 0 0.75rem;
        }
      }
    }
  }
}

.shares {
  .shares-card {
    padding: 1.5rem;
    margin-top: 1rem;

    .shares-header {
      display: flex;
      margin-bottom: 0.5rem;

      div {
        margin-right: 1rem;

        h3 {
          text-align: center;
          margin-bottom: 0.25rem;
        }

        select {
          width: 100%;
        }
      }

      div:last-child {
        //margin-left: auto;
        margin-right: 0;
      }
    }

    .body {
      margin-bottom: 1.5rem;

      overflow: auto;
      scrollbar-width: thin;
    }
  }
}

table {
  border-collapse: collapse;
  table-layout: fixed;
  width:100%;

  tr {
    th, td {
      text-align: right;

      background: var(--pure-white);
      width: 160px;

      padding: 0.25rem 0.75rem;
    }

    > :first-child {
      text-align: left;

      left: 0;
      z-index: 1;
      position: sticky;
      width: 280px;
    }

    &:not(:first-of-type), &:not(:last-of-type) {
      td:first-of-type {
        border-left: 1px solid variables.$charcoal-tint-20;
      }
    }
  }

  thead {
    tr {
      font-weight: bold;

      th {
        text-align: center;
      }
    }
  }

  tbody {
    border-top: 1px solid variables.$charcoal-tint-20;
    border-bottom: 1px solid variables.$charcoal-tint-20;

    tr {
      td {
        border-right: 1px solid variables.$charcoal-tint-20;
      }
    }
  }

  tfoot {
    tr {
      font-weight: bold;

      > :not(:first-child) {
        color: var(--highlight-color);
      }
    }
  }
}

.border-t {
  border-top: 1px solid variables.$charcoal-tint-20;
}

span.next-year,
span.prev-year,
.overview-header-mobile,
.grant-groups-body-mobile,
.grant-groups-footer-mobile {
  display: none;
}

@media screen and (max-width: 500px) {
  .current-vested-unvested, .grant-groups-body, .grant-groups-footer {
    display: none !important;
  }

  .grant-groups-header {
    justify-content: center !important;
    border-bottom: none !important;
    padding-bottom: 0px !important;
    margin-bottom: 0px !important;
  }

  .grant-groups-footer-mobile {
    display: unset;
    text-align: center;
    margin-top: 1rem;
  }

  .grant-groups-body-mobile {
    display: unset;
    select {
      width: 100%;
      margin-bottom: 1vw;
    }
  }

  .mobile-chart {
    display: unset;
  }

  .sidebar .totals {
    display: flex !important;
    align-items: center;
    flex-direction: column;

    p {
      padding: 0 !important;
    }
  }
}

@media screen and (max-width: 825px) {
  #projections-table {
    th, td {
      width: unset;
    }
    th:not(.active), td:not(.active){
      display: none;
    }
    th.active, td.active{
      display: table-cell;
    }

    th.active:not(:first-child) {
      font-size: 24px;
    }
  
    span.next-year, span.prev-year {
      display: unset;
      text-decoration: underline;
      cursor: pointer;
      font-size: 24px;
      color: #241b89;
      &.disabled {
        color: variables.$color-button-disabled;
        cursor: default;
      }
    }

    th span.next-year {
      padding-left: 8px;
    }
    th span.prev-year {
      padding-right: 8px;
    }
  }
}

@media screen and (max-width: 1199px) {
  .sidebar-header {
    grid-column-end: 8 !important;
  }

  .overview-header {
    display: none !important;
  }

  .overview-header-mobile {
    display: grid;
    color: var(--foreground-color);
    background-color: var(--brand-color);
    padding: 0.5vw 0;
    text-align: center;
  }

  .graph-content {
    height: unset !important;
  }

  .grid-cols-3 {
    display: flex !important;
    flex-direction: column;
  }
}
</style>